// Factures fournisseurs : saisie, échéances, paiements.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { achatsAPI, comptaAPI } from '../../services/api';
import { apiError, dateFr, fcfa, today, PAYMENT_MODES } from '../../utils/format';
import { tokens } from '../../theme';

const STATUT = {
  A_PAYER: { label: 'À payer', color: 'warning' }, PARTIEL: { label: 'Payée en partie', color: 'info' },
  PAYEE: { label: 'Payée', color: 'success' }, ANNULEE: { label: 'Annulée', color: 'default' },
};
const num = (v) => Number(String(v ?? '').replace(/[\s  ]/g, '').replace(',', '.')) || 0;

const Kpi = ({ label, value, color }) => (
  <Card variant="outlined"><CardContent sx={{ py: '12px !important' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography sx={{ fontFamily: tokens.mono, fontSize: 22, fontWeight: 600, color }}>{fcfa(value)} F</Typography>
  </CardContent></Card>
);

const FacturesFournisseurs = () => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ factures: [], resume: null });
  const [fournisseurs, setFournisseurs] = useState([]);
  const [filters, setFilters] = useState({ statut: 'A_REGLER', fournisseur_id: '' });
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pay, setPay] = useState(null);
  const [bcs, setBcs] = useState([]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    comptaAPI.facturesF(p).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { achatsAPI.fournisseurs().then((r) => setFournisseurs(r.data)).catch(() => {}); }, []);

  const openDetail = useCallback(async (id) => {
    try { const r = await comptaAPI.factureF(id); setDetail(r.data); setPay({ date_paiement: today(), montant: String(r.data.reste), mode: 'VIREMENT', reference: '', error: '' }); } catch (e) { setError(apiError(e)); }
  }, []);

  const loadBcs = (fournisseurId) => {
    if (!fournisseurId) { setBcs([]); return; }
    achatsAPI.commandes({ fournisseur_id: fournisseurId }).then((r) => setBcs(r.data.filter((b) => !['BROUILLON', 'ANNULE'].includes(b.statut)))).catch(() => {});
  };
  const openForm = (init = {}) => {
    setForm({ fournisseur_id: '', bc_id: '', numero: '', date_facture: today(), date_echeance: '', objet: '', montant_ht: '', montant_tva: '', commentaire: '', error: '', ...init });
    loadBcs(init.fournisseur_id);
  };

  // Liens entrants : ?bc=ID (saisir la facture d'un bon de commande) ou ?id=ID (ouvrir une facture)
  useEffect(() => {
    const bc = params.get('bc');
    const id = params.get('id');
    if (bc) {
      achatsAPI.commande(bc).then(({ data: b }) => {
        const reste = Math.max(0, b.total_ttc - b.total_facture);
        const ratio = b.total_ttc ? reste / b.total_ttc : 0;
        openForm({ fournisseur_id: b.fournisseur_id, bc_id: b.id, objet: `Commande ${b.numero}`,
          montant_ht: String(Math.round(b.total_ht * ratio)), montant_tva: String(Math.round(b.total_tva * ratio)) });
      }).catch((e) => setError(apiError(e)));
      setParams({}, { replace: true });
    } else if (id) {
      openDetail(Number(id));
      setParams({}, { replace: true });
    }
  }, [params, setParams, openDetail]);

  const saveForm = async () => {
    try {
      const body = { ...form, montant_ht: num(form.montant_ht), montant_tva: num(form.montant_tva) };
      const r = await comptaAPI.createFactureF(body);
      setForm(null); setToast(r.data.message); load();
    } catch (e) { setForm({ ...form, error: apiError(e) }); }
  };
  const savePay = async () => {
    try {
      await comptaAPI.payerFactureF(detail.id, { ...pay, montant: num(pay.montant) });
      setToast('Paiement enregistré'); load(); openDetail(detail.id);
    } catch (e) { setPay({ ...pay, error: apiError(e) }); }
  };
  const delPay = async (p) => {
    if (!window.confirm(`Supprimer le paiement de ${fcfa(p.montant)} F du ${dateFr(p.date_paiement)} ?`)) return;
    try { await comptaAPI.deletePaiementF(detail.id, p.id); load(); openDetail(detail.id); } catch (e) { setPay({ ...pay, error: apiError(e) }); }
  };
  const cancel = async () => {
    const motif = window.prompt("Motif de l'annulation de cette facture :");
    if (!motif) return;
    try { await comptaAPI.annulerFactureF(detail.id, motif); setDetail(null); setToast('Facture annulée'); load(); } catch (e) { setPay({ ...pay, error: apiError(e) }); }
  };

  const r = data.resume;
  return (
    <Layout title="Factures fournisseurs" plain
      actions={<Button variant="contained" startIcon={<AddOutlined />} onClick={() => openForm()}>Saisir une facture</Button>}>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {r && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><Kpi label="Total facturé (sélection)" value={r.total_ttc} /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Déjà payé" value={r.total_paye} color="#1B7A46" /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Reste à payer" value={r.reste} color="#B7791F" /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Dont échu" value={r.en_retard} color="#B4232A" /></Grid>
        </Grid>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Statut" value={filters.statut} sx={{ minWidth: 180 }} onChange={(e) => setFilters({ ...filters, statut: e.target.value })}>
          <MenuItem value="A_REGLER">À régler</MenuItem>
          <MenuItem value="EN_RETARD">Échues non payées</MenuItem>
          <MenuItem value="PAYEE">Payées</MenuItem>
          <MenuItem value="ANNULEE">Annulées</MenuItem>
          <MenuItem value="">Toutes</MenuItem>
        </TextField>
        <TextField select size="small" label="Fournisseur" value={filters.fournisseur_id} sx={{ minWidth: 220 }} onChange={(e) => setFilters({ ...filters, fournisseur_id: e.target.value })}>
          <MenuItem value="">Tous</MenuItem>
          {fournisseurs.map((f) => <MenuItem key={f.id} value={f.id}>{f.nom}</MenuItem>)}
        </TextField>
      </Box>
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Échéance</TableCell><TableCell>Fournisseur</TableCell><TableCell>N° facture</TableCell><TableCell>Date</TableCell><TableCell>Commande</TableCell>
                <TableCell align="right">TTC</TableCell><TableCell align="right">Payé</TableCell><TableCell align="right">Reste</TableCell><TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.factures.map((f) => (
                <TableRow key={f.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(f.id)}>
                  <TableCell sx={{ color: f.en_retard ? 'error.main' : undefined, whiteSpace: 'nowrap', fontWeight: f.en_retard ? 600 : 400 }}>{dateFr(f.date_echeance)}</TableCell>
                  <TableCell>{f.fournisseur}</TableCell>
                  <TableCell sx={{ fontFamily: tokens.mono }}>{f.numero}</TableCell>
                  <TableCell>{dateFr(f.date_facture)}</TableCell>
                  <TableCell sx={{ fontFamily: tokens.mono, fontSize: 12.5 }}>{f.bc_numero}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{fcfa(f.montant_ttc)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{fcfa(f.total_paye)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', fontWeight: 600 }}>{fcfa(f.reste)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUT[f.statut].label} color={STATUT[f.statut].color} />
                    {f.en_retard && <Chip size="small" label="Échue" color="error" variant="outlined" sx={{ ml: 0.5 }} />}
                  </TableCell>
                </TableRow>
              ))}
              {data.factures.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>Aucune facture</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Saisie */}
      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        {form && (
          <>
            <DialogTitle>Saisir une facture fournisseur</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {form.error && <Alert severity="error" sx={{ mb: 2 }}>{form.error}</Alert>}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12} sm={7}>
                  <TextField select fullWidth size="small" label="Fournisseur" value={form.fournisseur_id}
                    onChange={(e) => { setForm({ ...form, fournisseur_id: e.target.value, bc_id: '' }); loadBcs(e.target.value); }}>
                    {fournisseurs.map((f) => <MenuItem key={f.id} value={f.id}>{f.nom}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField select fullWidth size="small" label="Bon de commande (facultatif)" value={form.bc_id} onChange={(e) => {
                    const b = bcs.find((x) => x.id === e.target.value);
                    setForm({ ...form, bc_id: e.target.value, ...(b ? { montant_ht: String(b.total_ht), montant_tva: String(b.total_tva), objet: form.objet || `Commande ${b.numero}` } : {}) });
                  }}>
                    <MenuItem value="">Aucun</MenuItem>
                    {bcs.map((b) => <MenuItem key={b.id} value={b.id}>{b.numero} — {fcfa(b.total_ttc)} F</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="N° de facture" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.date_facture} onChange={(e) => setForm({ ...form, date_facture: e.target.value })} /></Grid>
                <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Échéance" InputLabelProps={{ shrink: true }} value={form.date_echeance}
                  helperText="Vide : délai du fournisseur" onChange={(e) => setForm({ ...form, date_echeance: e.target.value })} /></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" label="Objet" value={form.objet} placeholder="Électricité, gasoil, entretien…" onChange={(e) => setForm({ ...form, objet: e.target.value })} /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" label="Montant HT" value={form.montant_ht} inputProps={{ inputMode: 'decimal' }} onChange={(e) => setForm({ ...form, montant_ht: e.target.value })} /></Grid>
                <Grid item xs={4}><TextField fullWidth size="small" label="TVA" value={form.montant_tva} inputProps={{ inputMode: 'decimal' }} onChange={(e) => setForm({ ...form, montant_tva: e.target.value })} /></Grid>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Total TTC</Typography>
                  <Typography sx={{ fontFamily: tokens.mono, fontWeight: 700 }}>{fcfa(num(form.montant_ht) + num(form.montant_tva))} F</Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setForm(null)}>Annuler</Button>
              <Button variant="contained" disabled={!form.fournisseur_id || !form.numero} onClick={saveForm}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Détail et paiements */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="sm">
        {detail && pay && (
          <>
            <DialogTitle>
              {detail.fournisseur} — facture {detail.numero}
              <Chip size="small" label={STATUT[detail.statut].label} color={STATUT[detail.statut].color} sx={{ ml: 1 }} />
            </DialogTitle>
            <DialogContent>
              {pay.error && <Alert severity="error" sx={{ mb: 2 }}>{pay.error}</Alert>}
              <Typography variant="body2">
                Du {dateFr(detail.date_facture)}, échéance {dateFr(detail.date_echeance)}{detail.bc_numero ? ` · ${detail.bc_numero}` : ''}{detail.objet ? ` · ${detail.objet}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                HT {fcfa(detail.montant_ht)} + TVA {fcfa(detail.montant_tva)} = <b>{fcfa(detail.montant_ttc)} F TTC</b> · reste <b>{fcfa(detail.reste)} F</b>
              </Typography>
              {detail.motif_annulation && <Alert severity="warning" sx={{ mt: 1 }}>Annulée : {detail.motif_annulation}</Alert>}
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Mode</TableCell><TableCell>Référence</TableCell><TableCell align="right">Montant</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>
                  {detail.paiements.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{dateFr(p.date_paiement)}</TableCell><TableCell>{PAYMENT_MODES[p.mode]}</TableCell><TableCell>{p.reference}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa(p.montant)}</TableCell>
                      <TableCell padding="checkbox"><IconButton size="small" aria-label="Supprimer le paiement" onClick={() => delPay(p)}><DeleteOutline fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                  {detail.paiements.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary' }}>Aucun paiement</TableCell></TableRow>}
                </TableBody>
              </Table>
              {detail.reste > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={pay.date_paiement} onChange={(e) => setPay({ ...pay, date_paiement: e.target.value })} />
                  <TextField size="small" label="Montant" value={pay.montant} sx={{ width: 130 }} onChange={(e) => setPay({ ...pay, montant: e.target.value })} />
                  <TextField select size="small" label="Mode" value={pay.mode} sx={{ width: 140 }} onChange={(e) => setPay({ ...pay, mode: e.target.value })}>
                    {Object.entries(PAYMENT_MODES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Référence (n° chèque…)" value={pay.reference} sx={{ flex: 1, minWidth: 140 }} onChange={(e) => setPay({ ...pay, reference: e.target.value })} />
                  <Button variant="contained" onClick={savePay}>Enregistrer le paiement</Button>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {!detail.annulee && detail.paiements.length === 0 && <Button color="error" onClick={cancel} sx={{ mr: 'auto' }}>Annuler la facture</Button>}
              <Button onClick={() => setDetail(null)}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default FacturesFournisseurs;
